export const DEFAULT_CHILD_MIN_AGE = 1;
export const DEFAULT_CHILD_MAX_AGE = 13;

export const useChildAges = (guests: Ref<{ children: number, children_age: any[] }>, accommodation: Ref<any>) => {
  const { t } = useI18n();
  const showMissingAgeErrors = ref(false);
  const hasAdjustedAges = ref(false);

  const childMinAge = computed(() => accommodation.value?.min_child_age ?? DEFAULT_CHILD_MIN_AGE);

  const childMaxAge = computed(() => accommodation.value?.max_child_age ?? DEFAULT_CHILD_MAX_AGE);

  const shownAges = computed(() => Array.from({ length: guests.value.children }, (_, index) => guests.value.children_age?.[index]));

  const hasMissingAge = computed(() => shownAges.value.some(isMissingAge));

  const childAgeErrors = computed(() => shownAges.value.map(age => {
    if (showMissingAgeErrors.value && isMissingAge(age)) return t('formErrors.childAgeRequired');
    return null;
  }));

  function isMissingAge(age: any) {
    return [undefined, null, ''].includes(age) || isNaN(Number(age));
  }

  function fitAgesToLimits() {
    const ages = guests.value.children_age;
    if (!ages) return;
    ages.slice(0, guests.value.children).forEach((age, index) => {
      if (isMissingAge(age)) return;
      const fittedAge = Math.min(Math.max(Number(age), childMinAge.value), childMaxAge.value);
      if (fittedAge !== Number(age)) {
        ages[index] = fittedAge;
        hasAdjustedAges.value = true;
      }
    });
  }

  function setNewAgesToMin(count: number, oldCount: number) {
    if (!guests.value.children_age || count <= oldCount) return;
    for (let index = oldCount; index < count; index++) {
      if (isMissingAge(guests.value.children_age[index])) guests.value.children_age[index] = childMinAge.value;
    }
  }

  function resetChildAgeMessages() {
    showMissingAgeErrors.value = false;
    hasAdjustedAges.value = false;
  }

  watch([childMinAge, childMaxAge], () => {
    hasAdjustedAges.value = false;
  });

  watch(() => guests.value.children, setNewAgesToMin);

  watch([childMinAge, childMaxAge, () => guests.value.children, () => guests.value.children_age], fitAgesToLimits, { immediate: true });

  return {
    childMinAge,
    childMaxAge,
    childAgeErrors,
    hasMissingAge,
    showMissingAgeErrors,
    hasAdjustedAges,
    resetChildAgeMessages,
  };
};
